import React, { useEffect, useState } from "react";
import { FileText, Plus, Settings2, Pencil, Search, AlertTriangle, X, ChevronDown, ChevronRight, Users, Building2, Filter, Eye, ShieldCheck, Layers, Star, RotateCcw, Mail, Upload, CheckSquare } from "lucide-react";
import PortalNav from "../components/PortalNav";
import CreateRequestModal from "../components/CreateRequestModal";
import EditClientModal from "../components/EditClientModal";
import ImportRequestsModal from "../components/ImportRequestsModal";
import RequestWorkflowPanel from "../components/RequestWorkflowPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { StatusBadge, EmptyState, isOverdue, OverdueBadge, STATUS, ALL_STATUSES } from "../components/shared";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const EmployeePortal = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState([]); // client ids; empty = all
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [areaFilter, setAreaFilter] = useState("all");
  const [starredOnly, setStarredOnly] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showRequest, setShowRequest] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [importClient, setImportClient] = useState(null);
  const [picked, setPicked] = useState([]); // request ids for bulk notify
  const [recurOpen, setRecurOpen] = useState(false);
  const [recurInterval, setRecurInterval] = useState("monthly");
  const [recurDays, setRecurDays] = useState(30);
  const [recurNotify, setRecurNotify] = useState(true);
  const [recurSaving, setRecurSaving] = useState(false);

  const load = async () => {
    try {
      const [c, r] = await Promise.all([api.get("/clients"), api.get("/requests")]);
      setClients(c.data.clients); setRequests(r.data.requests);
    } catch (e) { /* handled */ }
  };
  useEffect(() => { load(); }, []);

  const isStarred = (r) => (r.bookmarks || []).includes(user?.id);
  const toggleStar = async (e, rid) => {
    e.stopPropagation();
    try { await api.post(`/requests/${rid}/bookmark`); await load(); }
    catch { toast({ title: "Could not update bookmark", variant: "destructive" }); }
  };
  const togglePick = (e, rid) => { e.stopPropagation(); setPicked((p) => (p.includes(rid) ? p.filter((x) => x !== rid) : [...p, rid])); };
  const notifyPicked = async () => {
    try { const { data } = await api.post("/requests/notify", { request_ids: picked }); toast({ title: "Emails sent", description: `${data.notified} notification(s) queued.` }); setPicked([]); }
    catch { toast({ title: "Could not send notifications", variant: "destructive" }); }
  };
  const applyBulkRecurring = async () => {
    setRecurSaving(true);
    try {
      const { data } = await api.post("/requests/bulk-recurring", {
        request_ids: picked, recurring: true, recur_interval: recurInterval,
        recur_days: Number(recurDays) || 0, recur_notify: recurNotify,
      });
      toast({ title: "Recurring turned on", description: `${data.updated} request(s) will auto-recreate ${recurInterval === "custom" ? `every ${recurDays} days` : recurInterval}.` });
      setRecurOpen(false); setPicked([]); await load();
    } catch (e) {
      toast({ title: "Could not set recurring", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setRecurSaving(false); }
  };

  const areas = Array.from(new Set(requests.map((r) => (r.area || "").trim()).filter(Boolean))).sort();
  const activeClientIds = selected.length ? selected : clients.map((c) => c.id);

  const statsFor = (cid) => {
    const rs = requests.filter((r) => r.client_id === cid);
    return {
      total: rs.length,
      pending: rs.filter((r) => r.status === STATUS.PENDING).length,
      submitted: rs.filter((r) => r.status === STATUS.SUBMITTED).length,
      closed: rs.filter((r) => r.status === STATUS.CLOSED).length,
      resubmit: rs.filter((r) => r.status === STATUS.RESUBMIT).length,
    };
  };

  const summaryClients = clients.filter((c) => activeClientIds.includes(c.id));
  const totals = summaryClients.reduce((acc, c) => {
    const s = statsFor(c.id);
    return { total: acc.total + s.total, pending: acc.pending + s.pending, submitted: acc.submitted + s.submitted, closed: acc.closed + s.closed, resubmit: acc.resubmit + s.resubmit };
  }, { total: 0, pending: 0, submitted: 0, closed: 0, resubmit: 0 });

  const q = search.trim().toLowerCase();
  const visibleRequests = requests.filter((r) => {
    if (!activeClientIds.includes(r.client_id)) return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (areaFilter !== "all" && (r.area || "").trim() !== areaFilter) return false;
    if (starredOnly && !(r.bookmarks || []).includes(user?.id)) return false;
    if (q) {
      const hay = `${r.title || ""} ${r.client_name || ""} ${r.project_name || ""} ${r.area || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const toggleClient = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const clearFilters = () => { setSearch(""); setStatusFilter("all"); setAreaFilter("all"); };
  const selLabel = selected.length === 0 ? "All clients" : selected.length === 1 ? (clients.find((c) => c.id === selected[0])?.name || "1 client") : `${selected.length} clients`;

  return (
    <div className="min-h-screen bg-slate-50/60 dark:bg-slate-950">
      <PortalNav title="Request Center" />
      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1a1a2e] dark:text-slate-100">Command Center</h1>
            <p className="mt-1 text-slate-500 dark:text-slate-400">Track every client request, review documents and act — all in one place.</p>
          </div>
          {clients.length > 0 && (
            <div className="flex gap-3">
              <Button data-testid="manage-clients-btn" onClick={() => setManageOpen(true)} variant="outline" className="rounded-xl border-slate-300 dark:border-slate-700"><Settings2 className="mr-2 h-4 w-4" /> Manage clients</Button>
              <Button data-testid="new-request-btn" onClick={() => setShowRequest(true)} className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><Plus className="mr-2 h-4 w-4" /> New request</Button>
            </div>
          )}
        </div>

        {clients.length === 0 ? (
          <div className="mt-10"><EmptyState title="No clients assigned yet" subtitle="Your firm admin hasn't assigned any clients to you yet." /></div>
        ) : (
          <>
            {/* Multi-select client filter */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="client-multiselect" className="inline-flex h-9 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-[#1a1a2e] hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                    <Users className="h-4 w-4 text-slate-400" /> {selLabel} <ChevronDown className="h-4 w-4 text-slate-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 rounded-xl p-2">
                  <div className="flex items-center justify-between px-2 py-1 text-xs text-slate-400">
                    <span>Filter by client</span>
                    {selected.length > 0 && <button data-testid="clear-clients" onClick={() => setSelected([])} className="font-medium text-[#2f2b8f] hover:underline">Reset</button>}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {clients.map((c) => (
                      <label key={c.id} data-testid={`client-opt-${c.id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
                        <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggleClient(c.id)} />
                        <span className="truncate text-[#1a1a2e] dark:text-slate-200">{c.name}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              {selected.length > 0 && (
                <button data-testid="clear-clients-chip" onClick={() => setSelected([])} className="inline-flex h-9 items-center gap-1 rounded-xl border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-500 hover:text-rose-600 dark:border-slate-700 dark:bg-slate-900">
                  <X className="h-3.5 w-3.5" /> Clear ({selected.length})
                </button>
              )}
              <span className="text-xs text-slate-400">Summary and requests below reflect your selection</span>
            </div>

            {/* Summary per-client table */}
            <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead><tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400 dark:border-slate-800">
                    <th className="px-6 py-3 font-semibold">Client</th>
                    <th className="px-4 py-3 text-center font-semibold">Total</th>
                    <th className="px-4 py-3 text-center font-semibold">Pending from Client</th>
                    <th className="px-4 py-3 text-center font-semibold">Client Submitted</th>
                    <th className="px-4 py-3 text-center font-semibold">Accepted & Closed</th>
                    <th className="px-4 py-3 text-center font-semibold">Resubmission Requested</th>
                    <th className="px-6 py-3 text-right font-semibold">Manage</th>
                  </tr></thead>
                  <tbody>
                    {summaryClients.map((c) => {
                      const s = statsFor(c.id);
                      return (
                        <tr key={c.id} data-testid={`summary-row-${c.id}`} onClick={() => setSelected([c.id])} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50/60 dark:border-slate-800/60 dark:hover:bg-slate-800/40">
                          <td className="px-6 py-3.5">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-[#1a1a2e] dark:text-slate-100">{c.name}</span>
                              {c.my_access === "view" && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300"><Eye className="h-2.5 w-2.5" /> View</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3.5 text-center font-semibold text-[#1a1a2e] dark:text-slate-200">{s.total}</td>
                          <td className="px-4 py-3.5 text-center"><span className={s.pending ? "font-semibold text-amber-600" : "text-slate-300"}>{s.pending}</span></td>
                          <td className="px-4 py-3.5 text-center"><span className={s.submitted ? "font-semibold text-blue-600" : "text-slate-300"}>{s.submitted}</span></td>
                          <td className="px-4 py-3.5 text-center"><span className={s.closed ? "font-semibold text-emerald-600" : "text-slate-300"}>{s.closed}</span></td>
                          <td className="px-4 py-3.5 text-center"><span className={s.resubmit ? "font-semibold text-rose-600" : "text-slate-300"}>{s.resubmit}</span></td>
                          <td className="px-6 py-3.5 text-right"><div className="flex items-center justify-end gap-1.5">
                            <Button data-testid={`summary-import-${c.id}`} onClick={(e) => { e.stopPropagation(); setImportClient(c); }} size="sm" variant="outline" className="h-8 rounded-lg text-xs dark:border-slate-700"><Upload className="mr-1 h-3 w-3" /> Import</Button>
                            <Button data-testid={`summary-manage-${c.id}`} onClick={(e) => { e.stopPropagation(); setEditClient(c); }} size="sm" variant="outline" className="h-8 rounded-lg text-xs dark:border-slate-700"><Pencil className="mr-1 h-3 w-3" /> Manage</Button>
                          </div></td>
                        </tr>
                      );
                    })}
                    <tr className="bg-slate-50/70 font-semibold dark:bg-slate-800/40">
                      <td className="px-6 py-3 text-[#1a1a2e] dark:text-slate-200">Total ({summaryClients.length})</td>
                      <td className="px-4 py-3 text-center text-[#1a1a2e] dark:text-slate-200">{totals.total}</td>
                      <td className="px-4 py-3 text-center text-amber-600">{totals.pending}</td>
                      <td className="px-4 py-3 text-center text-blue-600">{totals.submitted}</td>
                      <td className="px-4 py-3 text-center text-emerald-600">{totals.closed}</td>
                      <td className="px-4 py-3 text-center text-rose-600">{totals.resubmit}</td>
                      <td className="px-6 py-3" />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Requests light table (expandable) */}
            <div className="mt-8 flex flex-wrap items-center gap-2">
              <h2 className="mr-2 text-lg font-bold text-[#1a1a2e] dark:text-slate-100">Requests</h2>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="req-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="h-9 w-52 rounded-xl pl-9" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="req-filter-status" className="h-9 w-52 rounded-xl"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={areaFilter} onValueChange={setAreaFilter}>
                <SelectTrigger data-testid="req-filter-area" className="h-9 w-40 rounded-xl"><SelectValue placeholder="Area" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All areas</SelectItem>
                  {areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
              {(search || statusFilter !== "all" || areaFilter !== "all") && (
                <button data-testid="req-filter-clear" onClick={clearFilters} className="inline-flex h-9 items-center gap-1 rounded-xl px-2 text-xs font-medium text-slate-500 hover:text-rose-600"><X className="h-3.5 w-3.5" /> Clear</button>
              )}
              <button data-testid="starred-filter" onClick={() => setStarredOnly((v) => !v)} className={`inline-flex h-9 items-center gap-1.5 rounded-xl border px-3 text-xs font-semibold transition-colors ${starredOnly ? "border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300" : "border-slate-200 bg-white text-slate-500 dark:border-slate-700 dark:bg-slate-900"}`}>
                <Star className={`h-3.5 w-3.5 ${starredOnly ? "fill-amber-500 text-amber-500" : ""}`} /> Starred
              </button>
              <span className="ml-auto text-xs text-slate-400">{visibleRequests.length} request(s)</span>
            </div>

            {picked.length > 0 && (
              <div data-testid="bulk-action-bar" className="mt-3 flex items-center justify-between rounded-xl border border-indigo-100 bg-indigo-50/70 px-4 py-2.5 dark:border-indigo-500/20 dark:bg-indigo-500/10">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-indigo-800 dark:text-indigo-300"><CheckSquare className="h-4 w-4" /> {picked.length} selected</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPicked([])} className="text-xs font-medium text-slate-500 hover:text-slate-700">Clear</button>
                  <Button data-testid="bulk-recurring-btn" onClick={() => setRecurOpen(true)} size="sm" variant="outline" className="rounded-xl border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-500/30 dark:text-violet-300"><RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Make recurring</Button>
                  <Button data-testid="bulk-notify-btn" onClick={notifyPicked} size="sm" className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]"><Mail className="mr-1.5 h-3.5 w-3.5" /> Notify clients</Button>
                </div>
              </div>
            )}

            <Dialog open={recurOpen} onOpenChange={setRecurOpen}>
              <DialogContent data-testid="bulk-recurring-dialog" className="rounded-2xl sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Make {picked.length} request(s) recurring</DialogTitle>
                  <DialogDescription>A fresh copy is auto-created each cycle, with the period auto-drafted into its title and description.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-1">
                  <div className="space-y-1.5">
                    <Label>Frequency</Label>
                    <Select value={recurInterval} onValueChange={setRecurInterval}>
                      <SelectTrigger data-testid="bulk-recur-interval" className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="fortnightly">Fortnightly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="halfyearly">Half-yearly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                        <SelectItem value="custom">Custom (days)</SelectItem>
                      </SelectContent>
                    </Select>
                    {recurInterval === "custom" && (
                      <Input data-testid="bulk-recur-days" type="number" min="1" value={recurDays} onChange={(e) => setRecurDays(e.target.value)} placeholder="Number of days" className="mt-2 rounded-xl" />
                    )}
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/40">
                    <div>
                      <div className="text-sm font-medium text-[#1a1a2e] dark:text-slate-200">Notify client each time</div>
                      <div className="text-xs text-slate-500">Email the client whenever a new cycle is auto-created.</div>
                    </div>
                    <Switch data-testid="bulk-recur-notify" checked={recurNotify} onCheckedChange={setRecurNotify} />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setRecurOpen(false)} className="rounded-xl">Cancel</Button>
                  <Button data-testid="bulk-recur-apply" onClick={applyBulkRecurring} disabled={recurSaving} className="rounded-xl bg-[#2f2b8f] text-white hover:bg-[#26236f]">{recurSaving ? "Applying..." : "Turn on recurring"}</Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="mt-3 space-y-2">
              {visibleRequests.length === 0 ? (
                <div data-testid="no-requests" className="rounded-2xl border border-dashed border-slate-200 bg-white/60 p-12 text-center dark:border-slate-700 dark:bg-slate-900/40">
                  <Filter className="mx-auto h-8 w-8 text-slate-300" />
                  <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No requests match your selection</p>
                </div>
              ) : visibleRequests.map((r) => {
                const open = expandedId === r.id;
                return (
                  <div key={r.id} data-testid={`request-${r.id}`} className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex w-full items-center gap-3 px-5 py-4 text-left">
                      <Checkbox data-testid={`pick-${r.id}`} checked={picked.includes(r.id)} onCheckedChange={() => {}} onClick={(e) => togglePick(e, r.id)} />
                      <button data-testid={`star-${r.id}`} onClick={(e) => toggleStar(e, r.id)} className="flex-shrink-0" title="Bookmark to get status-change emails">
                        <Star className={`h-4 w-4 ${isStarred(r) ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"}`} />
                      </button>
                      <button onClick={() => setExpandedId(open ? null : r.id)} className="flex min-w-0 flex-1 items-center gap-3">
                        {open ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-slate-400" /> : <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-400" />}
                        <div className="min-w-0 flex-1 text-left">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                            <Building2 className="h-3.5 w-3.5" /> {r.client_name}
                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-300"><Layers className="h-2.5 w-2.5" /> {r.project_name || "General"}</span>
                            {r.area && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300">{r.area}</span>}
                            {r.recurring && <span data-testid={`recurring-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300"><RotateCcw className="h-2.5 w-2.5" /> {r.recur_interval === "custom" ? `every ${r.recur_days}d` : r.recur_interval}</span>}
                          </div>
                          <h3 className="truncate text-sm font-bold text-[#1a1a2e] dark:text-slate-100">{r.seq != null && <span data-testid={`req-seq-${r.id}`} className="mr-1.5 font-mono text-xs font-semibold text-slate-400">#{r.seq}</span>}{r.title}</h3>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-2">
                          {isOverdue(r) && <OverdueBadge />}
                          {r.deadline && <span className="hidden text-xs text-slate-400 md:inline">Due {r.deadline}</span>}
                          <StatusBadge status={r.status} />
                        </div>
                      </button>
                    </div>
                    {open && (
                      <div className="border-t border-slate-100 bg-slate-50/40 p-5 dark:border-slate-800 dark:bg-slate-950/40">
                        <RequestWorkflowPanel request={r} onChanged={load} isClient={false} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      <CreateRequestModal open={showRequest} onClose={() => setShowRequest(false)} clients={clients} onCreated={load} onRefresh={load} onLimitReached={() => setShowRequest(false)} />

      <Dialog open={manageOpen} onOpenChange={(o) => !o && setManageOpen(false)}>
        <DialogContent data-testid="manage-clients-modal" className="sm:max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 dark:bg-indigo-500/15"><Settings2 className="h-6 w-6 text-indigo-700 dark:text-indigo-300" /></div>
            <DialogTitle className="text-2xl font-bold text-[#1a1a2e] dark:text-slate-100">Manage clients</DialogTitle>
            <DialogDescription className="text-slate-500">Add or remove client contacts and give other firm members access.</DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            {clients.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/50">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-[#1a1a2e] dark:text-slate-200">{c.name}</div>
                  <div className="text-xs text-slate-500">{(c.contacts || []).length || 1} contact(s) · {c.nature_of_engagement}</div>
                </div>
                <Button data-testid={`manage-edit-${c.id}`} onClick={() => { setEditClient(c); setManageOpen(false); }} variant="outline" size="sm" className="rounded-xl border-slate-300 dark:border-slate-700"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <EditClientModal open={!!editClient} client={editClient} mode="manage" onClose={() => setEditClient(null)} onUpdated={load} />
      <ImportRequestsModal open={!!importClient} client={importClient} onClose={() => setImportClient(null)} onImported={load} />
    </div>
  );
};

export default EmployeePortal;
